/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import { Actions } from "actions/types";
import { DEFAULT_STORE } from "store/types";

import * as UpduoAnalytics from "config/UpduoAnalytics";

export function createReducer<State, Store = typeof DEFAULT_STORE>(
  initialState: State,
  handlers: Partial<{
    [actionType in keyof Actions<Store>]: (
      state: State,
      action: Actions<Store>[actionType]
    ) => State;
  }>
): (state: State, action) => State {
  return function reducer(state = initialState, action) {
    if (handlers.hasOwnProperty(action.type)) {
      const newState = handlers[action.type](state, action);
      try {
        if (newState === undefined) {
          throw new Error("Invalid Redux state");
        }
      } catch (e) {
        const errorPayload = { state, action };
        if (e instanceof Error) {
          Object.assign(errorPayload, { stack: e.stack });
        }
        UpduoAnalytics.logEventNoContext("app-redux-error", errorPayload);
      }
      return newState;
    } else {
      return state;
    }
  };
}

export function createSetter<
  Action extends keyof Actions<Store>,
  Store = typeof DEFAULT_STORE
>(
  initialState: Actions<Store>[Action]["payload"],
  actionType: Actions<Store>[Action]["type"]
): (
  state: Actions<Store>[Action]["payload"],
  action: Actions<Store>[Action]
) => Actions<Store>[Action]["payload"] {
  return (state = initialState, action) =>
    action.type === actionType ? action.payload : state;
}
